import { useMemo } from "react"
import { map } from "lodash"
import { useOutletContext } from "react-router-dom"
import { percentageFormatter } from "helpers/formatters/numbers"
import { useLastRouterMatchProp } from "hooks/router"
import { createFactorSensitivitySingleDateQuery } from "gatherers/single-date/factor-sensitivity"
import { createFactorSensitivityMultiDateQuery } from "gatherers/multi-date/factor-sensitivity"
import CompareGraph from "../renderers/compare-graph"
import DataIntegratedSection from "./data-integrated-section"
import { useSelectedMetric } from "../hooks/selected-metric"

export default function CompareFactorSensitivity() {
  const context = useOutletContext()
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, modelMetrics: availableMetrics } = context
  const factorSensitivityType = useLastRouterMatchProp("factorSensitivityType")

  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)

  const currentDate = selectedDate || endDate

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    // Compare has no intraday mode; pass false explicitly rather than relying on the arg being omitted.
    metrics: map(positionSets, set => createFactorSensitivityMultiDateQuery(modelId, set.queryPositionSet, set.queryBase, startDate, endDate, factorSensitivityType, false)),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata: positionSets,
    tickFormatter: (value) => percentageFormatter(value, false),
    separator: true,
  }), [positionSets, availableMetrics, selectedMetric, modelId, startDate, endDate, factorSensitivityType])

  const sections = useMemo(
    () => {
      let firstExpandable = true
      return [
        graph,
        ...map(modelMetadata.categories, category => {
          let defaultExpanded
          if (firstExpandable && !category.singleFactor) {
            defaultExpanded = true
            firstExpandable = false
          }

          return {
            id: category.id,
            name: category.label,
            color: category.color,
            defaultExpanded,
            isGroup: true,
            sorted: true,
            metrics: map(positionSets, set => createFactorSensitivitySingleDateQuery(modelId, set.queryPositionSet, set.queryBase, currentDate, factorSensitivityType, { category: category.id })),
          }
        }),
      ]
    },
    // [TODO] how to improve reference integrity of position sets
    [graph, modelMetadata?.categories, positionSets, modelId, currentDate, factorSensitivityType],
  )

  return sections.map((section, index) => {
    const configKey = `sensitivity-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={percentageFormatter}
      />
    )
  })
}
